import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { SingleValue, ActionMeta } from "react-select";
import Navbar from "../navbar/Navbar";
import "./Workrecords.css";
import "../form/ReactSelect.css";

import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { appendAlert } from "../../redux/alertSlice";

import { PlantCodeSelect } from "../ui/PlantCodeSelect";
import { ProcCodeSelect } from "../ui/ProcCodeSelect";
import { MachCodeSelect } from "../ui/MachCodeSelect";
import { MemberSuggestSelect } from "../ui/MemberSuggestSelect";
import Footer from "../footer/Footer";
import apiAxios from "../../utils/apiAxios";
import Grid from "../grid/WorkRecordsGrid";
import Spinner from "../ui/Spinner";
import RippleButton from "../ui/rippleButton";
import { GridApi } from "ag-grid-enterprise";
import { forEach } from "lodash";
import { GetWorkRecords } from "../../models/GetWorkRecords";
import { isAuthorized } from "../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";

interface PostWorkrecord {
  id: number;
  plant_code: string;
  proc_code: string;
  mach_code: string;
  member_id: string;
  member_proc_code: string;
  status_code: number;
  start_time: string;
  end_time: string;
  duration: number;
  update_by: string;
}

const Workrecords: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const userId = useSelector(
    (state: RootState) => state.user.userInfo?.user_id ?? ""
  );
  // 権限
  const permissions = useSelector(
    (state: RootState) => state.user.userInfo?.permissions ?? []
  );
  useEffect(() => {
    // 機械打刻修正権限が存在する場合のみ表示可能
    const authorized = isAuthorized("2", permissions);
    if (!authorized && userId !== "") {
      dispatch(
        appendAlert({
          severity: "error",
          message: "作業打刻を修正する権限はありません。",
          displayTime: 5000,
        })
      );
      navigate("/");
    }
  }, [userId]);

  const plantCode = useSelector(
    (state: RootState) => state.queryParams.selectedPlantCode
  ); // 製造工場コード
  const procCode = useSelector(
    (state: RootState) => state.queryParams.selectedProcCode
  ); // 加工コード
  const machCode = useSelector(
    (state: RootState) => state.queryParams.machCode
  ); // 機械作業コード

  // 初期値を設定
  const [selectPlantCode, setSelectPlantCode] = useState(plantCode); // 工場コード
  const [selectedProcCode, setSelectedProcCode] = useState(procCode); // 加工コード
  const [selectedMachCode, setSelectedMachCode] = useState(
    machCode && machCode.length === 1 ? machCode[0] : ""
  ); // 機械作業コード
  const [selectedMemberId, setSelectedMemberId] = useState(""); // 社員コード

  // セレクトボックスの変更を検知しstateの状態を更新
  // 工場コードの変更を検知
  const handlePlantCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectPlantCode(e.target.value);
    setSelectedProcCode("");
    setSelectedMachCode("");
    setSelectedMemberId("");
    fetchRowData(gridApi!, e.target.value, null, null, null);
  };
  // 加工コードの変更を検知
  const handleProcCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectedProcCode(e.target.value);
    setSelectedMachCode("");
    let memberId = selectedMemberId;
    if (e.target.value) {
      // 加工コードが変更されたらクリアする。
      // 加工コードがクリアされた場合は変更しない。
      setSelectedMemberId("");
      memberId = "";
    }
    fetchRowData(gridApi!, selectPlantCode, e.target.value, null, memberId);
  };
  // 機械作業コードの変更を検知
  const handleMachCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectedMachCode(e.target.value);
    fetchRowData(
      gridApi!,
      selectPlantCode,
      selectedProcCode,
      e.target.value,
      selectedMemberId
    );
  };
  // 社員コードの変更を検知
  const handleMemberIdChange = (
    selectedOption: SingleValue<any>,
    actionMeta: ActionMeta<any>
  ) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectedMemberId(selectedOption.value);
    fetchRowData(
      gridApi!,
      selectPlantCode,
      selectedProcCode,
      selectedMachCode,
      selectedOption.value
    );
  };

  // GridのAPI
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const fetchRowData = async (
    api: GridApi,
    plant_code: string | null,
    proc_code: string | null,
    mach_code: string | null,
    member_id: string | null
  ) => {
    api.setRowData([]);
    if (!plant_code) {
      return;
    }
    let data = await getWorkRecords(
      plant_code,
      proc_code,
      mach_code,
      member_id
    );
    if (data) {
      data = data.map((elem: any) => {
        return {
          ...elem,
          is_add: false,
          is_edit_member_id: false,
          is_edit_start_time: false,
          is_edit_end_time: false,
          is_edit_suspension: false,
          is_edit_duration: false,
          is_error_member_id: false,
          is_error_start_time: false,
          is_error_end_time: false,
          is_error_suspension: false,
          is_error_duration: false,
          error_message: "",
        };
      });
      api.applyTransaction({ add: data });
    }
  };

  // Gridの初期化時のコールバック
  const onGridReady = (params: { api: GridApi }) => {
    setGridApi(params.api);
    (async () => {
      await fetchRowData(
        params.api,
        selectPlantCode,
        selectedProcCode,
        selectedMachCode,
        selectedMemberId
      );
      params.api.sizeColumnsToFit();
    })();
  };

  const [isLoading, setIsLoading] = useState(false);

  // 作業実績の取得
  const getWorkRecords = async (
    plant_code: string | null,
    proc_code: string | null,
    mach_code: string | null,
    member_id: string | null
  ) => {
    try {
      setIsLoading(true);
      const response = await apiAxios.get<GetWorkRecords[]>(`/work_records`, {
        params: {
          plant_code: plant_code,
          proc_code: proc_code,
          mach_code: mach_code,
          member_id: member_id,
        },
      });
      // 終了時刻が入っていないものは修正対象外として除く
      return response.data.filter((d) => d.end_time !== "");
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの取得に失敗しました。" + error.message,
          displayTime: 5000,
        })
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // 編集行があるかチェックする
  const hasEditData = () => {
    let hasEdit = false;
    gridApi!.forEachNode((node: any) => {
      let data = node.data;
      let isEdit = data.is_add;
      forEach(data, (value, key) => {
        if (key.startsWith("is_edit_")) {
          isEdit = isEdit || value;
        }
      });
      hasEdit = hasEdit || isEdit;
    });
    return hasEdit;
  };

  // 作業実績の登録
  const registWorkRecords = async () => {
    let rowData: any[] = [];
    gridApi!.forEachNode((node: any) => {
      // 変更・追加された行のみを取得
      let data = node.data;

      let isEdit = data.is_add;
      forEach(data, (value, key) => {
        if (key.startsWith("is_edit_")) {
          isEdit = isEdit || value;
        }
      });
      if (isEdit) {
        rowData.push(data);
      }
    });

    if (rowData.length === 0) {
      dispatch(
        appendAlert({
          severity: "error",
          message: "変更されたデータがありません。",
          displayTime: 5000,
        })
      );
      return;
    }

    // エラーがあるかチェックする
    let isError = false;
    rowData.forEach((row: any) => {
      forEach(row, (value, key) => {
        if (key.startsWith("is_error_") && value) {
          isError = true;
        }
      });
    });

    if (isError) {
      dispatch(
        appendAlert({
          severity: "error",
          message:
            "入力エラーがあります。★が付いている行のデータを確認してください。",
          displayTime: 5000,
        })
      );
      return;
    }

    let postData: PostWorkrecord[] = [];
    rowData.forEach((elem: any) => {
      if (elem.id < 0) {
        // 新規追加行
        postData.push({
          id: 0,
          plant_code: elem.plant_code,
          proc_code: elem.proc_code,
          mach_code: elem.mach_code,
          member_id: elem.member_id,
          member_proc_code: elem.member_proc_code,
          status_code: elem.status_code,
          start_time: elem.start_time,
          end_time: elem.end_time,
          duration: elem.duration,
          update_by: userId,
        });
      } else {
        // 変更行
        postData.push({
          id: elem.id,
          plant_code: elem.plant_code,
          proc_code: elem.proc_code,
          mach_code: elem.mach_code,
          member_id: elem.member_id,
          member_proc_code: elem.member_proc_code,
          status_code: elem.status_code,
          start_time: elem.start_time,
          end_time: elem.end_time,
          duration: elem.duration,
          update_by: userId,
        });
      }
    });

    // 作業実績の登録
    try {
      await apiAxios.post(`/work_records`, postData);
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの登録に失敗しました。。" + error.message,
          displayTime: 5000,
        })
      );
      return;
    }

    // データを再取得する
    await fetchRowData(
      gridApi!,
      selectPlantCode,
      selectedProcCode,
      selectedMachCode,
      selectedMemberId
    );

    dispatch(
      appendAlert({
        severity: "success",
        message: "データを登録しました。",
        displayTime: 5000,
      })
    );
  };

  return (
    <div>
      <Helmet>
        <title>Process Flow Manager</title>
      </Helmet>
      <Navbar />
      <div className="workrecords-container">
        <div className="workrecords-container-title">
          <img src="images/clock.svg" alt="" />
          <img src="images/time-card.svg" alt="" />
          <h1>作業打刻修正</h1>
        </div>

        <div className="workrecords-container-navi">
          <div className="workrecords-container-navi-item">
            <PlantCodeSelect
              label="工場"
              selectPlantCode={selectPlantCode}
              handlePlantCodeChange={handlePlantCodeChange}
            />
          </div>
          <div className="workrecords-container-navi-item">
            <ProcCodeSelect
              label="加工"
              plantCode={selectPlantCode}
              selectProcCode={selectedProcCode}
              handleProcCodeChange={handleProcCodeChange}
            />
          </div>
          <div className="workrecords-container-navi-item">
            <MachCodeSelect
              label="機械"
              plantCode={selectPlantCode}
              procCode={selectedProcCode}
              selectMachCode={selectedMachCode}
              handleMachCodeChange={handleMachCodeChange}
            />
          </div>
          <div className="workrecords-container-navi-item">
            <MemberSuggestSelect
              label="作業者"
              selectMenberId={selectedMemberId}
              plantCode={selectPlantCode}
              teamCode={selectedProcCode}
              handleMemberChange={handleMemberIdChange}
            />
          </div>
        </div>
        <div className="workrecords-container-grid">
          {isLoading && <Spinner />}
          <Grid onGridReadyParent={onGridReady} />
        </div>
        <div className="workrecords-reg-btn">
          <RippleButton
            width={"300px"}
            heihgt={"45px"}
            borderRadius={"8px"}
            onClick={registWorkRecords}
            effectSize={160}
            className="bk-secondary"
          >
            <span className="children">登録</span>
          </RippleButton>
        </div>
      </div>
      {/* フッター */}
      <Footer />
    </div>
  );
};

export default Workrecords;
